/**
 * ============================================================
 * constants.ts — Constantes Compartilhadas da Aplicacao
 * ============================================================
 *
 * Responsabilidade: Centralizar labels, cores, estilos e
 *   configuracoes reutilizadas em multiplas paginas e
 *   componentes. Evita duplicacao e garante consistencia.
 *
 * Conteudo:
 *   - Colunas padrao do pipeline Kanban
 *   - Chaves do localStorage
 *   - Paleta de cores (HSL real, nao CSS vars — para Canvas/Recharts)
 *   - Labels e estilos de prioridade, etapa, porte, categoria
 *   - Tipos de log de contato (icones e labels)
 *   - Fontes, servicos, plataformas, territorios
 *   - Estilos de tooltip/badge para graficos
 *   - Labels de atividade, auditoria e severidade
 *   - Helpers utilitarios (toggleChip, getScoreColor, clearAppCache)
 *
 * Quando usar: Importe daqui em vez de redefinir inline.
 * ============================================================
 */

import type React from 'react';
import type { KanbanColumnDef } from '@/store/types';

// ── Colunas Padrao do Pipeline Kanban ──
/** Colunas iniciais do pipeline CRM criadas ao iniciar um projeto */
/**
 * Funil exibido enquanto as colunas do projeto não chegaram do banco.
 *
 * Espelha exatamente o que o trigger `seed_new_project` grava (migration
 * 20260922100007), inclusive o `stageType`. Duas razões para não divergir:
 *
 *   • sem `stageType`, o tipo saía de `inferStageType()` — que acerta nestes
 *     rótulos, mas é adivinhação, e a regra da casa é ler o tipo, não inferir;
 *   • a lista antiga tinha 5 colunas e NENHUMA terminal negativa, então nesse
 *     intervalo não havia para onde mandar um lead perdido.
 */
export const DEFAULT_KANBAN_COLUMNS: KanbanColumnDef[] = [
  { id: 'prospeccao', label: 'Prospecção', order: 0, color: 'hsl(226, 100%, 51%)', stageType: 'open' },
  { id: 'qualificacao', label: 'Qualificação', order: 1, color: 'hsl(260, 70%, 60%)', stageType: 'qualified' },
  { id: 'reuniao', label: 'Reunião', order: 2, color: 'hsl(200, 70%, 50%)', stageType: 'meeting' },
  { id: 'proposta', label: 'Proposta', order: 3, color: 'hsl(38, 92%, 50%)', stageType: 'proposal' },
  { id: 'negociacao', label: 'Negociação', order: 4, color: 'hsl(0, 84%, 60%)', stageType: 'negotiation' },
  { id: 'ganho', label: 'Ganho', order: 5, color: 'hsl(152, 69%, 40%)', stageType: 'won' },
  { id: 'perdido', label: 'Perdido', order: 6, color: 'hsl(215, 15%, 55%)', stageType: 'lost' },
];

// ── Chaves do localStorage ──
/** Chaves usadas para persistencia local — prefixo `bl-` para evitar colisoes */
export const STORAGE_KEYS = {
  THEME: 'bl-theme',
  ACTIVE_WORKSPACE_ID: 'bl-activeWorkspaceId',
  ACTIVE_PROJECT_ID: 'bl-activeProjectId',
  ACTIVE_PROJECT_TYPE: 'bl-activeProjectType',
  SIDEBAR_COLLAPSED: 'sidebar-collapsed',
  PENDING_INVITE: 'pending_invite',
} as const;

import { Phone, Mail, MessageCircle, Calendar, FileText, ShoppingCart, BookOpen, Briefcase, Stethoscope, MoreHorizontal } from 'lucide-react';

// ── Cores ──
// NOTA: Usamos valores HSL reais (nao CSS vars) porque Canvas API e Recharts
// nao conseguem resolver custom properties CSS. Devem ser strings de cor reais.
export const COLORS = {
  BLUE: 'hsl(226, 100%, 51%)',
  BLUE_LIGHT: 'hsl(226, 80%, 65%)',
  GREEN: 'hsl(152, 69%, 40%)',
  PURPLE: 'hsl(260, 70%, 60%)',
  AMBER: 'hsl(38, 92%, 50%)',
  RED: 'hsl(0, 84%, 60%)',
  PRIMARY: 'hsl(226, 100%, 51%)',
  SECONDARY: 'hsl(240, 5%, 96%)',
} as const;

// ── Priority ──
export const PRIORITY_LABELS: Record<string, string> = { high: 'Alta', medium: 'Média', low: 'Baixa' };
export const PRIORITY_STYLES: Record<string, string> = {
  high: 'bg-destructive/15 text-destructive border-destructive/30',
  medium: 'bg-warning/15 text-warning border-warning/30',
  low: 'bg-success/15 text-success border-success/30',
};

// ── Stages ──
export const STAGE_LABELS: Record<string, string> = {
  prospeccao: 'Prospecção', qualificacao: 'Qualificação', proposta: 'Proposta',
  negociacao: 'Negociação', fechamento: 'Fechamento',
};
export const STAGE_STYLES: Record<string, string> = {
  prospeccao: 'bg-warning/15 text-warning border-warning/30',
  qualificacao: 'bg-primary/15 text-primary border-primary/30',
  proposta: 'bg-accent-foreground/15 text-accent-foreground border-accent-foreground/30',
  negociacao: 'bg-destructive/15 text-destructive border-destructive/30',
  fechamento: 'bg-success/15 text-success border-success/30',
};

// ── Porte / Category ──
export const PORTE_LABELS: Record<string, string> = { pequeno: 'Pequeno', medio: 'Médio', grande: 'Grande', enterprise: 'Enterprise' };
export const CATEGORY_LABELS: Record<string, string> = { 'e-commerce': 'E-commerce', 'infoproduto': 'Infoproduto', 'b2b': 'B2B', 'clinica_medica': 'Clínica', 'outros': 'Outros' };

export const CATEGORY_CONFIG: Record<string, { label: string; icon: typeof Briefcase; color: string }> = {
  'e-commerce': { label: 'E-commerces', icon: ShoppingCart, color: COLORS.BLUE },
  'infoproduto': { label: 'Infoprodutos', icon: BookOpen, color: COLORS.PURPLE },
  'b2b': { label: 'B2B', icon: Briefcase, color: COLORS.GREEN },
  'clinica_medica': { label: 'Clínicas Médicas', icon: Stethoscope, color: COLORS.AMBER },
  'outros': { label: 'Outros', icon: MoreHorizontal, color: COLORS.RED },
};

// ── Contact Log Types ──
export const LOG_TYPE_ICONS: Record<string, typeof Phone> = { call: Phone, email: Mail, whatsapp: MessageCircle, meeting: Calendar, note: FileText };
export const LOG_TYPE_LABELS: Record<string, string> = { call: 'Ligação', email: 'Email', whatsapp: 'WhatsApp', meeting: 'Reunião', note: 'Nota' };

// ── Lead Sources / Services ──
export const ALL_SOURCES = ['Google Ads', 'Meta Ads', 'LinkedIn', 'Orgânico', 'Indicação', 'Email Marketing', 'TikTok Ads', 'Leads Antigos'];
export const ALL_SERVICES = ['Tráfego Pago', 'Data Analytics', 'Desenvolvimento de Produto', 'Web Analytics'];
export const ALL_PLATFORMS = ['Google Ads', 'Meta Ads', 'LinkedIn Ads', 'TikTok Ads', 'YouTube Ads', 'Pinterest Ads', 'Taboola', 'Outbrain', 'Shopify', 'WooCommerce', 'Hotmart', 'Kiwify'];
export const ALL_STAGES = ['Prospecção', 'Qualificação', 'Proposta', 'Negociação', 'Fechamento'];
export const ALL_STAGE_KEYS = ['prospeccao', 'qualificacao', 'proposta', 'negociacao', 'fechamento'];

// ── Territories ──
export const ALL_TERRITORIES = ['Sul', 'Sudeste', 'Centro-Oeste', 'Nordeste', 'Norte', 'Nacional', 'Internacional'];

// ── Recharts Tooltip Style (theme-aware) ──
export const CHART_TOOLTIP_STYLE: React.CSSProperties = {
  borderRadius: 12,
  border: '1px solid hsl(var(--border))',
  fontSize: 12,
  background: 'hsl(var(--card))',
  color: 'hsl(var(--foreground))',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
};

// ── Activity / Audit Labels ──
export const ACTIVITY_ACTION_LABELS: Record<string, string> = {
  created: 'criou',
  updated: 'atualizou',
  status_change: 'mudou status',
  assigned: 'atribuiu',
  comment: 'comentou em',
  time_entry: 'registrou tempo em',
  call: 'ligou para',
  email: 'enviou email para',
  whatsapp: 'enviou WhatsApp para',
  meeting: 'teve reunião com',
  note: 'adicionou nota em',
};

export const TASK_STATUS_LABELS: Record<string, string> = {
  todo: 'A Fazer',
  in_progress: 'Em Progresso',
  review: 'Em Revisão',
  done: 'Concluído',
};

export const AUDIT_ACTION_LABELS: Record<string, string> = {
  login: 'Login', logout: 'Logout',
  role_change: 'Alteração de Permissão', member_added: 'Membro Adicionado', member_removed: 'Membro Removido',
  lead_deleted: 'Lead Excluído', task_deleted: 'Task Excluída', client_deleted: 'Cliente Excluído', epic_deleted: 'Épico Excluído',
  lead_created: 'Lead Criado', task_created: 'Task Criada', client_created: 'Cliente Criado',
  settings_changed: 'Config. Alterada', api_token_regenerated: 'Token Regenerado',
  integration_connected: 'Integração Conectada', integration_disconnected: 'Integração Desconectada',
  bulk_delete: 'Exclusão em Massa', data_exported: 'Dados Exportados', data_imported: 'Dados Importados',
  filter_saved: 'Filtro Salvo', filter_deleted: 'Filtro Removido',
};

export const AUDIT_ACTION_SEVERITY: Record<string, 'info' | 'warning' | 'critical'> = {
  login: 'info', logout: 'info',
  role_change: 'warning', member_added: 'info', member_removed: 'warning',
  lead_deleted: 'warning', task_deleted: 'warning', client_deleted: 'warning', epic_deleted: 'warning',
  lead_created: 'info', task_created: 'info', client_created: 'info',
  settings_changed: 'warning', api_token_regenerated: 'critical',
  integration_connected: 'info', integration_disconnected: 'warning',
  bulk_delete: 'critical', data_exported: 'info', data_imported: 'info',
  filter_saved: 'info', filter_deleted: 'info',
};

// ── Helpers ──
/** Alterna um valor em uma lista de chips/filtros selecionados */
export function toggleChip(list: string[], setList: (v: string[]) => void, val: string) {
  setList(list.includes(val) ? list.filter(v => v !== val) : [...list, val]);
}

/** Retorna cor HSL baseada no score (0-100): verde para alto, vermelho para baixo */
export function getScoreColor(score: number): string {
  if (score >= 80) return 'hsl(var(--success))';
  if (score >= 60) return 'hsl(var(--ring))';
  if (score >= 40) return COLORS.PURPLE;
  if (score >= 20) return 'hsl(var(--warning))';
  return 'hsl(var(--destructive))';
}

/** Clears all app-related data from localStorage, sessionStorage, and service worker caches. */
export async function clearAppCache(): Promise<void> {
  // Clear app-specific localStorage keys
  Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));

  // Clear sessionStorage
  sessionStorage.clear();

  // Unregister service workers and clear caches
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((r) => r.unregister()));
  }
  if ('caches' in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  }
}
